import {
  Bell,
  Globe,
  Loader2,
  Button as DesignSystemButton,
} from "@ews-admin/global-design-system";
import { zodResolver } from "@hookform/resolvers/zod";
import React, { useCallback, useEffect, useState } from "react";
import { Controller, useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";
import { Select } from "../components/atoms/Select";
import { ChangePinSection } from "../components/molecules/ChangePinSection/ChangePinSection";
import { LockDelaySection } from "../components/molecules/LockDelaySection/LockDelaySection";
import { ToggleSwitch } from "../components/atoms/ToggleSwitch";
import {
  ADMIN_SETTINGS_LANGUAGES,
  AdminSettingsFormData,
  adminSettingsSchema,
} from "../schemas/adminAccount.schema";
import { userApi } from "../services/user.api";
import { useAppSelector } from "../store/hooks";
import {
  NOTIFICATION_CATEGORIES,
  NotificationCategory,
  UpdateUserSettingsPayload,
  UserSettings,
} from "../types/userSettings.definition";

type AdminLanguage = (typeof ADMIN_SETTINGS_LANGUAGES)[number];

/**
 * The live UI language as a `LanguageEnum` name. Deliberately derived from
 * i18next — not from the stored `appLanguage` — because MED-1446 was exactly
 * the bug where a value outside the i18next detector won over the choice the
 * administrator had made and was cached in localStorage. The settings page
 * makes the backend agree with the live language, never the other way round.
 */
export function toAdminLanguage(lng: string | undefined): AdminLanguage {
  return (lng ?? "").toUpperCase().startsWith("EN") ? "EN" : "FR";
}

/**
 * MED-1449: the administrator's own settings, reachable from the header user
 * menu at `/admin/settings` — the entry used to point at
 * `/manager/user-settings`, a dashboard-app route that 404s here.
 *
 * Backed by `GET`/`PUT /api/v1/users/{id}/settings`. Only the two settings
 * this application actually acts on are surfaced: the notification
 * categories, and the UI language. Theme, timezone and visibility are also
 * part of the payload — and NOT NULL on both backends, with the PUT
 * replacing every field — so they are read and written straight back instead
 * of being offered as controls the admin never honours.
 */
export const AdminSettings: React.FC = () => {
  const { t, i18n } = useTranslation();
  const userId = useAppSelector(state => state.session.session?.user?.id);

  const [settings, setSettings] = useState<UserSettings | null>(null);
  const [loading, setLoading] = useState(true);
  const [loadFailed, setLoadFailed] = useState(false);
  const [saving, setSaving] = useState(false);

  const {
    control,
    handleSubmit,
    reset,
    formState: { isDirty },
  } = useForm<AdminSettingsFormData>({
    resolver: zodResolver(adminSettingsSchema(t)),
    mode: "onChange",
    defaultValues: {
      appLanguage: toAdminLanguage(i18n.language),
      notifications: [],
    },
  });

  const load = useCallback(async () => {
    if (!userId) return;
    setLoading(true);
    setLoadFailed(false);
    try {
      const loaded = await userApi.getUserSettings(userId);
      setSettings(loaded);
      reset({
        appLanguage: toAdminLanguage(i18n.language),
        notifications: loaded.notifications ?? [],
      });
    } catch {
      // The axios interceptor already surfaces the error globally.
      setLoadFailed(true);
    } finally {
      setLoading(false);
    }
  }, [userId, reset, i18n.language]);

  useEffect(() => {
    void load();
  }, [load]);

  const onSubmit = async (data: AdminSettingsFormData) => {
    if (!userId) return;
    setSaving(true);
    try {
      const payload: UpdateUserSettingsPayload = {
        settingsId: settings?.settingsId ?? null,
        // Required by both backends; carried over untouched so saving
        // notifications or a language cannot wipe them.
        theme: settings?.theme ?? "LIGHT",
        timeZone: settings?.timeZone ?? "UTC",
        visibility: settings?.visibility ?? "PRIVATE",
        appLanguage: data.appLanguage,
        notifications: data.notifications,
        userId: settings?.userId ?? Number(userId),
      };
      const saved = await userApi.updateUserSettings(userId, payload);
      setSettings(saved);
      // Same mechanism as the header switcher: i18next caches the choice in
      // localStorage through its detector, so the live UI follows immediately
      // and survives a reload.
      await i18n.changeLanguage(data.appLanguage.toLowerCase());
      reset(data);
      toast.success(t("admin.settings.saved"));
    } catch {
      // Surfaced by the axios interceptor.
    } finally {
      setSaving(false);
    }
  };

  if (!userId) {
    return (
      <div className="p-6">
        <p className="text-sm text-error-600">
          {t("admin.settings.noSession")}
        </p>
      </div>
    );
  }

  return (
    <div className="p-6 space-y-6">
      <div>
        <h1 className="text-2xl font-bold text-neutral-900">
          {t("admin.settings.title")}
        </h1>
        <p className="text-sm text-neutral-600">
          {t("admin.settings.subtitle")}
        </p>
      </div>

      {loading && (
        <div className="flex items-center space-x-2 text-sm text-neutral-500">
          <Loader2 className="w-4 h-4 animate-spin" />
          <span>{t("common.loading")}</span>
        </div>
      )}

      {!loading && loadFailed && (
        <div className="p-4 bg-white rounded-xl border border-neutral-200">
          <p className="text-sm text-error-600">
            {t("admin.settings.loadError")}
          </p>
          <button
            type="button"
            onClick={() => void load()}
            className="mt-2 text-xs underline text-ews-primary"
          >
            {t("common.retry")}
          </button>
        </div>
      )}

      {!loading && !loadFailed && (
        <form onSubmit={handleSubmit(onSubmit)} className="space-y-6">
          <section className="p-5 space-y-4 bg-white rounded-xl border border-neutral-200">
            <div className="flex items-center space-x-2">
              <Globe className="w-4 h-4 text-neutral-500" />
              <h2 className="text-lg font-semibold text-neutral-900">
                {t("admin.settings.language.title")}
              </h2>
            </div>
            <p className="text-xs text-neutral-500">
              {t("admin.settings.language.hint")}
            </p>
            <Controller
              name="appLanguage"
              control={control}
              render={({ field }) => (
                <Select
                  label={t("admin.settings.language.label")}
                  value={field.value}
                  options={ADMIN_SETTINGS_LANGUAGES.map(code => ({
                    value: code,
                    label: t(`admin.settings.language.options.${code}`),
                  }))}
                  onChange={value => field.onChange(value as AdminLanguage)}
                />
              )}
            />
          </section>

          <section className="p-5 space-y-4 bg-white rounded-xl border border-neutral-200">
            <div className="flex items-center space-x-2">
              <Bell className="w-4 h-4 text-neutral-500" />
              <h2 className="text-lg font-semibold text-neutral-900">
                {t("admin.settings.notifications.title")}
              </h2>
            </div>
            <p className="text-xs text-neutral-500">
              {t("admin.settings.notifications.hint")}
            </p>
            <Controller
              name="notifications"
              control={control}
              render={({ field }) => (
                <div className="space-y-3">
                  {NOTIFICATION_CATEGORIES.map(category => {
                    const checked = field.value.includes(category);
                    return (
                      <div
                        key={category}
                        className="flex justify-between items-center"
                      >
                        <span className="text-sm text-neutral-800">
                          {t(
                            `admin.settings.notifications.categories.${category}`
                          )}
                        </span>
                        <ToggleSwitch
                          checked={checked}
                          ariaLabel={t(
                            `admin.settings.notifications.categories.${category}`
                          )}
                          onChange={next =>
                            field.onChange(
                              next
                                ? [...field.value, category]
                                : field.value.filter(
                                    (value: NotificationCategory) =>
                                      value !== category
                                  )
                            )
                          }
                        />
                      </div>
                    );
                  })}
                </div>
              )}
            />
          </section>

          <DesignSystemButton
            type="submit"
            loading={saving}
            disabled={saving || !isDirty}
          >
            {t("common.save")}
          </DesignSystemButton>
        </form>
      )}

      <ChangePinSection />
      <LockDelaySection />
    </div>
  );
};

export default AdminSettings;
